import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom';
import { initSupabase } from '@pc/db';
import { reportError } from '@pc/core';
import {
  RouterErrorElement,
  createAppQueryClient,
  lazyWithReload,
} from '@pc/ui';
import { env } from './env';
import { AuthProvider } from './auth/AuthProvider';

// Code-split routes. Same `lazyWithReload` pattern the four B2B apps use
// so a user with the old shell loaded after a deploy gets a debounced
// hard reload instead of "Failed to fetch dynamically imported module".
const LoginPage = lazyWithReload(() =>
  import('./routes/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazyWithReload(() =>
  import('./routes/SignupPage').then((m) => ({ default: m.SignupPage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import('./routes/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import('./routes/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const InvitePage = lazyWithReload(() =>
  import('./routes/InvitePage').then((m) => ({ default: m.InvitePage })),
);
const SetPasswordPage = lazyWithReload(() =>
  import('./routes/SetPasswordPage').then((m) => ({ default: m.SetPasswordPage })),
);
const SignoutPage = lazyWithReload(() =>
  import('./routes/SignoutPage').then((m) => ({ default: m.SignoutPage })),
);
const NotFoundPage = lazyWithReload(() =>
  import('./routes/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

// The auth site keeps its OWN segregated session bucket. `pc-auth.auth`
// must NEVER collide with any B2B app's storage key (those are
// `pc-{web,admin,sell,partners,shop}.auth`), otherwise the auth site
// would surface a user's app-side session as its own and skip the
// sign-in step incorrectly.
initSupabase({
  url: env.supabaseUrl,
  anonKey: env.supabaseAnonKey,
  storageKey: 'pc-auth.auth',
});

const queryClient = createAppQueryClient({
  onQueryError: (error, query) => {
    void reportError(error as unknown, {
      severity: 'error',
      tags: {
        source: 'react_query.query',
        query_key: Array.isArray(query.queryKey)
          ? query.queryKey.map((k) => (typeof k === 'string' ? k : JSON.stringify(k))).join(':')
          : String(query.queryKey),
      },
    });
  },
  onMutationError: (error) => {
    void reportError(error as unknown, {
      severity: 'error',
      tags: { source: 'react_query.mutation' },
    });
  },
});

const router = createBrowserRouter([
  {
    errorElement: <RouterErrorElement homeHref="/login" />,
    children: [
      // Bare root sends visitors to the sign-in form. Anyone bookmarking
      // https://auth.peptideclients.com lands somewhere meaningful.
      { path: '/', element: <Navigate to="/login" replace /> },
      // Compatibility shim for old marketing CTAs and email links that
      // still reference /sign-in. Same surface as /login.
      { path: '/sign-in', element: <Navigate to="/login" replace /> },
      { path: '/sign-up', element: <Navigate to="/signup" replace /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/invite/:token', element: <InvitePage /> },
      { path: '/set-password', element: <SetPasswordPage /> },
      { path: '/signout', element: <SignoutPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* future.v7_startTransition: auto-wrap every router state
            update in React.startTransition. Without this, clicking a
            <Link> that targets a lazy()-loaded route triggers React
            error #426 ("a component suspended while responding to
            synchronous input"). Mirror of the five B2B apps. */}
        <RouterProvider router={router} future={{ v7_startTransition: true }} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
